/* eslint-disable no-restricted-imports */
import React, {useState} from 'react'
import {Button} from '../../../common/Button/Button'
import styles from './Footer.module.scss'
import {CA} from '../../../../../../code/client/shared/scripts/analytics/Analytics'
import {getHomepageUrlWithParameter, navigateToPage} from '../../../../../../code/client/shared/scripts/ui_utils/ClientUtils'
import {T} from '../../../common/T'
import {useUIGameEndContext} from '../../../contexts/UIGameEndContext'
import {GameTypeEnum, ModeSettingEnum} from '@colonist/types'
import {EndGameButtonTypes, getEndGameRightButtonType} from '@colonist/game-endgame-types'
import {TranslationObject} from '@colonist/i18n'

import {socket} from '../../../../../../code/client/shared/scripts/EntryShared'
import {AnalyticEventType} from '../../../../../../code/shared/entity_datas/EntityDatas'
import {StorageWrapper} from '../../../../../../code/client/shared/scripts/StorageWrapper'
import {UIUrlParameterNames} from '../../../../../../code/client/shared/scripts/ui_html/UIUrlController'
import {UIEntryUrlParameterController} from '../../../../../../code/client/shared/scripts/UIEntryUrlParameterController'
import clsx from 'clsx'
import {FADE_IN_ANIMATION_DELAYS} from '../constants'
import {useUIGameManagerContext} from '../../../contexts/UIGameManagerContext'
import {FadeInContainer} from '../../../common/FadeInContainer'

// TODO: @Dang remove this when SocketWebActionController fully migrated to React.
let globalSetPlayersJoinedRematch: undefined | ((playersJoinedRematch: number) => void)
export function onSetPlayersJoinedRematch(playersJoinedRematch: number) {
    if(globalSetPlayersJoinedRematch) globalSetPlayersJoinedRematch(playersJoinedRematch)
}

export function Footer({disableAnimation}: {disableAnimation: boolean}) {
    const gameManager = useUIGameManagerContext()
    const {
        playersByRank,
        isPlayerHasRankedState,
        isAfterLiveGame,
        showPopupsBeforeExitingGame,
        setIsFirstClickExitGame,
        exitGameAndGoToHome,
    } = useUIGameEndContext()

    const [playersJoinedRematch, setPlayersJoinedRematch] = useState(0)
    globalSetPlayersJoinedRematch = setPlayersJoinedRematch

    const isReplay = gameManager.gameController.isReplay

    const onRematchClick = (text: TranslationObject) => {
        showPopupsBeforeExitingGame(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameRematch)
            const isSocketOpen = socket?.controller.isSocketOpen()
            if(isSocketOpen) {
                gameManager.socketGameSend.clickedRematch()
                return
            }
            window.location.hash = socket.gameListener.databaseGameId
            window.location.reload()
        }, text)
    }

    const onPlayClick = (text: TranslationObject) => {
        showPopupsBeforeExitingGame(() => {
            if(gameManager.gameController.gameDetails.matchmakingMatchType == undefined) {
                navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.FindGameSelector,
                    UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
                return
            }
            navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.EnterMatchmaking,
                gameManager.gameController.gameDetails.matchmakingMatchType))
        }, text)
    }

    const onRetryClick = async() => {
        StorageWrapper.decrementBeginnerModeLevel()
        await gameManager.socketGameSend.sendGameAnalyticOnce(AnalyticEventType.BeginnerModeClickedRetry,
            StorageWrapper.getBeginnerModeLevel())
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.Campaign, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
    }

    const onNextLevelClick = async() => {
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameRematch)
        await gameManager.socketGameSend.sendGameAnalyticOnce(AnalyticEventType.BeginnerModeClickedNextLevel, StorageWrapper.getBeginnerModeLevel())
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.Campaign, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
    }

    // TODO: 11 Feb 2024. @Oguz: Temporary solution for LobbyAction.Create1v1BotGame on Discord. Read #15023
    // This should be removed once the Discord flow is updated to handle the game creation from game end screen.
    const shouldHideRematchButtonFor1v1BotGame = () => {
        if(gameManager.gameController.isPlayerSpectator) return true
        if(!gameManager.gameController.gameDetails.isDiscord) return false
        if(gameManager.gameController.gameSettings.gameType != GameTypeEnum.BotGame) return false
        if(gameManager.gameController.botSessionCount() != 1) return false
        return true
    }

    const renderRightButton = () => {
        if(!(isAfterLiveGame || !isReplay)) return null

        const isCampaign = gameManager.gameController.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        const isMatchmakingGame = gameManager.gameController.gameSettings.gameType == GameTypeEnum.MatchmakingGame
            || gameManager.gameController.gameSettings.gameType == GameTypeEnum.MatchmakingGameRanked
        const isGameWonByHuman = gameManager.gameController.getPlayerState(playersByRank[0].color).isUser
        const rightButton: EndGameButtonTypes = getEndGameRightButtonType(isCampaign, isMatchmakingGame, isGameWonByHuman)

        if(shouldHideRematchButtonFor1v1BotGame()) {
            return null
        }

        let translation: TranslationObject = {key: 'strings:game.endGame.footer.continue'}
        let onClickAction
        switch(rightButton) {
            case EndGameButtonTypes.Rematch:
                translation = playersJoinedRematch ? {
                    key: 'strings:game.endGame.footer.playersJoined',
                    options: {
                        playerCount: playersJoinedRematch,
                        maxPlayerCount: gameManager.gameController.gameSettings.maxPlayers,
                    },
                } : {key: 'strings:game.endGame.footer.rematch'}
                onClickAction = () => {
                    setIsFirstClickExitGame(false)
                    onRematchClick(translation)
                }
                break
            case EndGameButtonTypes.MatchmakingPlayAgain:
                translation = {key: 'strings:game.endGame.footer.play'}
                onClickAction = () => {
                    setIsFirstClickExitGame(false)
                    onPlayClick(translation)
                }
                break
            case EndGameButtonTypes.Retry:
                translation = {key: 'strings:game.endGame.footer.retry'}
                onClickAction = () => {
                    setIsFirstClickExitGame(false)
                    void onRetryClick()
                }
                break
            case EndGameButtonTypes.NextLevel:
                translation = {key: 'strings:game.endGame.footer.nextLevel'}
                onClickAction = () => {
                    setIsFirstClickExitGame(false)
                    void onNextLevelClick()
                }
                break
            default:
                return null
        }

        return <Button className={styles.button} onClick={onClickAction}>
            <T id={translation} />
        </Button>
    }

    return (
        <FadeInContainer
            animationDelayMs={isPlayerHasRankedState && !isReplay ? FADE_IN_ANIMATION_DELAYS.footerRankedGame : FADE_IN_ANIMATION_DELAYS.footer}
            animationDurationMs={FADE_IN_ANIMATION_DELAYS.transitionDuration}
            className={styles.footerContainer}
            disableAnimation={disableAnimation}>
            <div className={clsx(styles.buttons)}>
                <Button
                    className={styles.button}
                    onClick={exitGameAndGoToHome}
                    variant='dark'>
                    <T id={{key: 'strings:game.endGame.footer.home'}} />
                </Button>
                {renderRightButton()}
            </div>
        </FadeInContainer>
    )
}
